import {
  ChevronLeft,
  ChevronRightOutlined,
  Groups2Outlined,
  HomeOutlined,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import FlexBetween from "components/FlexBetween";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useMatch, useNavigate } from "react-router-dom";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import CPD_Logo from "assets/images/CPD_Logo.png";
import { useDispatch, useSelector } from "react-redux";
import { checkRoles, getPermissionFlags } from "utils/permissions";
import { setLogout, setMode } from "reduxStore/state";

const SECTION_KEYS = {
  GUIDANCE: "guidance",
  ADMIN: "admin",
  ANALYSIS: "analysis",
  COACHING: "coaching",
};

const stringAvatar = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((p) => p?.[0] ?? "")
    .join("");
  return { children: initials || "?" };
};

function SidebarSection({
  section,
  activePath,
  openSection,
  onToggle,
  onNavigate,
  colors,
}) {
  const { key, label, icon: Icon, items } = section;
  const isOpen = openSection === key;
  const isActiveSection = items.some((item) => item.path === activePath);
  const isHighlighted = isActiveSection || isOpen;

  return (
    <>
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => onToggle(key)}
          sx={{
            position: "relative",
            borderRadius: "0.75rem",
            mx: "0.75rem",
            my: "0.25rem",
            backgroundColor: isHighlighted ? colors.sectionBg : "transparent",
            color: isHighlighted ? colors.primaryDark : colors.text,
            border: `1px solid ${
              isHighlighted ? colors.sectionBorder : "transparent"
            }`,
            boxShadow: isHighlighted ? "0 6px 16px -10px rgba(0,0,0,0.25)" : "none",
            py: "0.65rem",
            "&:hover": {
              backgroundColor: colors.sectionHover,
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "0.5rem",
              top: "50%",
              transform: "translateY(-50%)",
              width: 4,
              height: "70%",
              borderRadius: "999px",
              backgroundColor: isActiveSection
                ? colors.primary
                : isHighlighted
                ? colors.sectionBorder
                : "transparent",
            }}
          />
          <ListItemIcon
            sx={{
              ml: "1rem",
              minWidth: "2.5rem",
              color: isHighlighted ? colors.primaryDark : colors.text,
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "0.75rem",
                display: "grid",
                placeItems: "center",
                backgroundColor: isHighlighted
                  ? colors.iconBg
                  : colors.iconBgMuted,
                border: `1px solid ${colors.sectionBorder}`,
              }}
            >
              <Icon fontSize="small" />
            </Box>
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontWeight: isActiveSection ? 700 : 600,
            }}
          />
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={isOpen} timeout="auto" unmountOnExit>
        <List
          component="div"
          disablePadding
          sx={{
            mt: "0.25rem",
            mx: "0.75rem",
            mb: "0.5rem",
            py: "0.35rem",
            pl: "0.5rem",
            borderLeft: `2px solid ${colors.sectionBorder}`,
            backgroundColor: colors.subPanelBg,
            borderRadius: "0.65rem",
          }}
        >
          {items.map(({ text, path }) => {
            const isActive = activePath === path;
            return (
              <ListItem key={path} disablePadding>
                <ListItemButton
                  onClick={() => onNavigate(path)}
                  sx={{
                    mx: "0.25rem",
                    borderRadius: "0.5rem",
                    backgroundColor: isActive
                      ? colors.subActiveBg
                      : "transparent",
                    color: isActive ? colors.primaryDark : colors.subText,
                    pl: "2.75rem",
                    borderLeft: `4px solid ${
                      isActive ? colors.primaryDark : "transparent"
                    }`,
                    "&:hover": {
                      backgroundColor: colors.sectionHover,
                    },
                  }}
                >
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 700 : 500,
                      fontSize: "0.95rem",
                    }}
                  />
                  {isActive && (
                    <ChevronRightOutlined
                      sx={{ ml: "auto", color: colors.primaryDark }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Collapse>
    </>
  );
}

function Sidebar({
  drawerWidth,
  isSideBarOpen,
  setIsSideBarOpen,
  isLargeScreen,
}) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();

  const user = useSelector((state) => state.user);
  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "User";

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const { isAnyAdmin, showOrg, showSeniorLeader, showDivision, showLevel } =
    getPermissionFlags(user);
  const { isAcademyImprovementLead } = checkRoles(user);

  const { palette } = useTheme();
  const primary = palette.primary.main;
  const backgroundColor = palette.background.alt;
  const dark = palette.neutral.dark;
  const mediumLight = palette.neutral.mediumLight;
  const primaryDark = palette.primary.dark;
  const primaryLight = palette.primary.light;
  const sectionBg = alpha(primary, 0.08);
  const sectionHover = alpha(primary, 0.12);
  const subActiveBg = alpha(primaryLight, 0.2);
  const sectionBorder = alpha(primary, 0.35);
  const subPanelBg = alpha(primary, 0.04);
  const iconBg = alpha(primary, 0.12);
  const iconBgMuted = alpha(primary, 0.06);
  const subText = palette.neutral.medium;

  const isCoachingFeedMatch = useMatch("/coaching-feed/:userId");
  const isCoachingFeedRoot = useMatch("/coaching-feed");
  const isCoachingMatch = useMatch("/coaching");

  const guidanceChildren = useMemo(
    () => [
      { text: "Project Overview", path: "project-overview" },
      { text: "User Guidance", path: "home" },
      { text: "Deliberate Practice Guide", path: "deliberate-practice-guide" },
      {
        text: "Instructional Coaching Guide",
        path: "instructional-coaching-guide",
      },
    ],
    []
  );

  const adminChildren = useMemo(
    () =>
      [
        showOrg && { text: "Organisation", path: "organisation" },
        showOrg && { text: "Schools", path: "schools" },
        (showDivision || showSeniorLeader || isAcademyImprovementLead) && {
          text: "Subjects",
          path: "subjects",
        },
        (showLevel || showSeniorLeader || isAcademyImprovementLead) && {
          text: "Years",
          path: "years",
        },
        (isAnyAdmin || showSeniorLeader || isAcademyImprovementLead) && {
          text: "Users",
          path: "users",
        },
        (isAnyAdmin || showSeniorLeader || isAcademyImprovementLead) && {
          text: "T&L Principles",
          path: "tl-principles",
        },
        (isAnyAdmin || showSeniorLeader || isAcademyImprovementLead) && {
          text: "T&L Foci",
          path: "tl-foci",
        },
      ].filter(Boolean),
    [
      showOrg,
      showDivision,
      showSeniorLeader,
      isAcademyImprovementLead,
      showLevel,
      isAnyAdmin,
    ]
  );

  const analysisChildren = useMemo(
    () =>
      [
        (isAnyAdmin || showSeniorLeader || isAcademyImprovementLead) && {
          text: "T&L Principles Graph",
          path: "tl-principles-graph",
        },
        (isAnyAdmin || showSeniorLeader || isAcademyImprovementLead) && {
          text: "T&L Foci Graph",
          path: "tl-foci-graph",
        },
        (showSeniorLeader || showDivision || isAcademyImprovementLead) && {
          text: "Lesson visits",
          path: "lesson-visits",
        },
      ].filter(Boolean),
    [isAnyAdmin, showSeniorLeader, isAcademyImprovementLead, showDivision]
  );

  const coachingChildren = useMemo(
    () => [
      { text: "Coaching Table", path: "coaching" },
      { text: "Coaching Feed", path: "coaching-feed" },
    ],
    []
  );

  const sections = useMemo(() => {
    const sectionList = [
      {
        key: SECTION_KEYS.GUIDANCE,
        label: "Guidance",
        icon: HomeOutlined,
        items: guidanceChildren,
      },
      {
        key: SECTION_KEYS.ANALYSIS,
        label: "Analysis",
        icon: AssessmentOutlinedIcon,
        items: analysisChildren,
      },
      {
        key: SECTION_KEYS.COACHING,
        label: "Coaching",
        icon: AssignmentIndOutlinedIcon,
        items: coachingChildren,
      },
    ];

    if (isAnyAdmin) {
      sectionList.splice(1, 0, {
        key: SECTION_KEYS.ADMIN,
        label: "Admin",
        icon: Groups2Outlined,
        items: adminChildren,
      });
    }

    return sectionList.filter((section) => section.items.length > 0);
  }, [guidanceChildren, analysisChildren, coachingChildren, adminChildren, isAnyAdmin]);

  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    const last = pathname.split("/").filter(Boolean).pop() || "";

    const resolvedActive =
      isCoachingFeedMatch || isCoachingFeedRoot
        ? "coaching-feed"
        : isCoachingMatch
        ? "coaching"
        : last;

    setActive(resolvedActive);

    const match = sections.find((section) =>
      section.items.some((item) => item.path === resolvedActive)
    );
    setOpenSection(match?.key || null);
  }, [
    pathname,
    isCoachingFeedMatch,
    isCoachingFeedRoot,
    isCoachingMatch,
    sections,
  ]);

  const handleToggleSection = (key) => {
    setOpenSection((current) => (current === key ? null : key));
  };

  const handleNavigate = (path) => {
    navigate(path);
    setActive(path);
  };

  const drawerContent = (
    <Box component="nav">
      {isSideBarOpen && (
        <Drawer
          open={isSideBarOpen}
          onClose={() => setIsSideBarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: primary,
              backgroundColor,
              boxSizing: "border-box",
              borderWidth: isLargeScreen ? 0 : "2px",
              width: drawerWidth,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <Box m="1.5rem 2rem 2rem 3rem">
              <FlexBetween color={primary}>
                <IconButton
                  onClick={() => navigate("/home")}
                  sx={{
                    color: "grey",
                    backgroundColor: "transparent",
                    borderRadius: "1.25rem",
                    padding: "0.25rem 0.25rem 0.25rem 0.5rem",
                    border: `1px solid ${mediumLight}`,
                    "&:hover": { boxShadow: 3 },
                  }}
                >
                  <Box display="flex" alignItems="center" gap={0.75}>
                    <img
                      src={CPD_Logo}
                      alt="CPD Tracker"
                      style={{ cursor: "pointer", height: 32 }}
                    />
                    <Avatar
                      {...stringAvatar(fullName)}
                      sx={{ bgcolor: primaryDark }}
                    />
                  </Box>
                </IconButton>
                {!isLargeScreen && (
                  <IconButton onClick={() => setIsSideBarOpen(false)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>

            <List sx={{ flexGrow: 1 }}>
              {sections.map((section) => (
                <SidebarSection
                  key={section.key}
                  section={section}
                  activePath={active}
                  openSection={openSection}
                  onToggle={handleToggleSection}
                  onNavigate={handleNavigate}
                  colors={{
                    primary,
                    primaryLight,
                    primaryDark,
                    background: backgroundColor,
                    text: dark,
                    sectionBg,
                    sectionHover,
                    subActiveBg,
                    sectionBorder,
                    subPanelBg,
                    iconBg,
                    iconBgMuted,
                    subText,
                  }}
                />
              ))}
            </List>
            {/* 4) Bottom actions live in their own List */}
            <List sx={{ pt: 0 }}>
              {/* Theme toggle */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => dispatch(setMode())}
                  sx={{ color: dark }}
                >
                  <ListItemIcon sx={{ ml: "2rem", color: dark }}>
                    {palette.mode === "dark" ? (
                      <LightModeOutlinedIcon />
                    ) : (
                      <DarkModeOutlinedIcon />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      palette.mode === "dark"
                        ? "Switch to Light"
                        : "Switch to Dark"
                    }
                  />
                </ListItemButton>
              </ListItem>

              {/* Logout */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => setLogoutDialogOpen(true)}
                  sx={{ color: dark }}
                >
                  <ListItemIcon sx={{ ml: "2rem", color: dark }}>
                    <LogoutOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </List>

            {/* Logout confirmation dialog */}
            <Dialog
              open={logoutDialogOpen}
              onClose={() => setLogoutDialogOpen(false)}
            >
              <DialogTitle>Confirm Logout</DialogTitle>
              <DialogContent>
                <Typography>Are you sure you want to log out?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setLogoutDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    dispatch(setLogout());
                    navigate("/");
                  }}
                  color="error"
                  autoFocus
                >
                  Logout
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Drawer>
      )}
    </Box>
  );

  return !isLargeScreen && isSideBarOpen ? (
    <ClickAwayListener onClickAway={() => setIsSideBarOpen(false)}>
      {drawerContent}
    </ClickAwayListener>
  ) : (
    drawerContent
  );
}

export default Sidebar;
